import axios from "axios";
import { Button } from "components/button";
import Field from "components/field";
import { useModalContext } from "components/modal";
import Assignee from "components/react-select/assignees";
import DivisionDropdown from "components/react-select/division";
import { useFormik } from "formik";
import * as React from "react";
import { useMutation } from "react-query";
import * as Yup from "yup";
import { useTranslation } from 'react-i18next';
import { Checkbox } from "@material-ui/core";

type AssessAgingResult = {
  totalRecords: number;
  estimatedBatches: number;
  estimatedTimeSeconds: number;
  estimatedTimeMinutes: number;
  batchSize: number;
  willTimeout: boolean;
  message: string;
};

type TReport = {
  assignee: string[] | null;
  division: string[] | null;
  start_date: string | null;
  end_date: string | null;
  only_canceled: boolean | undefined;
  only_open_with_canceled_appointments: boolean | undefined;
  itemized: boolean | undefined;
};

type Props = {
  type: "agingList" | "salesList" | "inventoryUsageList" | "customerList" | "requestList" | "propertyList" | "invoiceList" | "appointmentList" | "commissionList" | "projectList";
};

export default function NewReportModal(props: Props) {
  const { t } = useTranslation();
  const tBase = "views.reports.newReportModal";
  const tr = (key: string) => t(`${tBase}.${key}`);
  const excludeAssigneeAndDivision = ["customerList", "requestList", "propertyList", "invoiceList", "appointmentList", "agingList", "commissionList", "projectList"];

  const modal = useModalContext();
  const [assessmentResult, setAssessmentResult] = React.useState<AssessAgingResult | null>(null);

  const mutation = useMutation(
    async (e: TReport) =>
      await axios
        .get(
          `${process.env.REACT_APP_SERVER_URL}/api/v2/company/reports/${props.type}`,
          {
            params: {
              ...e,
              start_date: new Date(e.start_date + " 0:00").toISOString(),
              end_date: new Date(e.end_date + " 0:00").toISOString(),
            },
          }
        )
        .then((res) => res.data),
    {
      onSuccess: (url) => {

        modal.setModal();
        window.location.assign(url);
      },
    }
  );

  const agingReportMutation = useMutation(
    async (e: TReport) => {
      return await axios
        .get(`${process.env.REACT_APP_SERVER_URL}/api/v2/company/reports/agingList`, {
          params: {
            ...e,
            start_date: new Date(e.start_date + " 0:00").toISOString(),
            end_date: new Date(e.end_date + " 0:00").toISOString(),
          },
          responseType: "blob",
        })
        .then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `AgingReport_${new Date().toISOString()}.xlsx`);
          document.body.appendChild(link);
          link.click();
        });
    },
    {
      onSuccess: () => {
        modal.setModal(); // Close modal after success
      },
      onError: (error) => {
        console.error("Error downloading aging report:", error);
      },
    }
  );

  const assessAgingMutation = useMutation(
    async (e: TReport) => {
      return await axios
        .get(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/reports/assessAging`, {
          params: {
            start_date: new Date(e.start_date + " 0:00").toISOString(),
            end_date: new Date(e.end_date + " 0:00").toISOString(),
          },
        })
        .then((response) => response.data);
    },
    {
      onSuccess: (data: AssessAgingResult) => {
        setAssessmentResult(data);
      },
      onError: () => {
        setAssessmentResult(null);
      },
    }
  );

  const assessAppointmentMutation = useMutation(
    async (e: TReport) => {
      return await axios
        .get(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/reports/assessAppointment`, {
          params: {
            start_date: new Date(e.start_date + " 0:00").toISOString(),
            end_date: new Date(e.end_date + " 0:00").toISOString(),
          },
        })
        .then((response) => response.data);
    },
    {
      onSuccess: (data: AssessAgingResult) => {
        setAssessmentResult(data);
      },
      onError: () => {
        setAssessmentResult(null);
      },
    }
  );

  const assessRequestMutation = useMutation(
    async (e: TReport) => {
      return await axios
        .get(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/reports/assessRequest`, {
          params: {
            start_date: new Date(e.start_date + " 0:00").toISOString(),
            end_date: new Date(e.end_date + " 0:00").toISOString(),
            only_canceled: e.only_canceled,
            only_open_with_canceled_appointments: e.only_open_with_canceled_appointments,
          },
        })
        .then((response) => response.data);
    },
    {
      onSuccess: (data: AssessAgingResult) => {
        setAssessmentResult(data);
      },
      onError: () => {
        setAssessmentResult(null);
      },
    }
  );

  const assessInvoiceMutation = useMutation(
    async (e: TReport) => {
      return await axios
        .get(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/reports/assessInvoice`, {
          params: {
            start_date: new Date(e.start_date + " 0:00").toISOString(),
            end_date: new Date(e.end_date + " 0:00").toISOString(),
          },
        })
        .then((response) => response.data);
    },
    {
      onSuccess: (data: AssessAgingResult) => {
        setAssessmentResult(data);
      },
      onError: () => {
        setAssessmentResult(null);
      },
    }
  );

  const commissionReportMutation = useMutation(
    async (e: TReport) => {
      return await axios
        .get(`${process.env.REACT_APP_SERVER_URL}/api/v2/company/reports/commissionList`, {
          params: {
            ...e,
            start_date: new Date(e.start_date + " 0:00").toISOString(),
            end_date: new Date(e.end_date + " 0:00").toISOString(),
          },
          responseType: "blob",
        })
        .then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `CommissionReport_${new Date().toISOString()}.xlsx`);
          document.body.appendChild(link);
          link.click();
        });
    },
    {
      onSuccess: () => {
        modal.setModal(); // Close modal after success
      },
      onError: (error) => {
        console.error("Error downloading commission report:", error);
      },
    }
  );
  
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - 30);
  const endDate = new Date();
  endDate.setDate(endDate.getDate() + 1);

  const initialValues = {
    assignee: null,
    division: null,
    start_date: startDate.toISOString().split("T")[0],
    end_date: endDate.toISOString().split("T")[0],
    only_canceled: false,
    only_open_with_canceled_appointments: false,
    itemized: false,
  };

  const onSubmit = (e: TReport) => {
    if (props.type === "agingList") {
      agingReportMutation.mutate(e);
    } else if (props.type === "commissionList") {
      commissionReportMutation.mutate(e);
    } else {
      mutation.mutate(e);
    }
  };

  const validationSchema = Yup.object({
    assignee: Yup.array(Yup.string()).nullable(),
    division: Yup.array(Yup.string()).nullable(),
    start_date: Yup.date().required(),
    end_date: Yup.date().required(),
    onlyCanceled: Yup.boolean(),
    only_open_with_canceled_appointments: Yup.boolean(),
    itemized: Yup.boolean(),
  });

  const formik = useFormik<TReport>({
    initialValues,
    onSubmit,
    validationSchema,
  });

  // Call assessment when date range changes for aging, appointmentList, requestList, and invoiceList reports
  React.useEffect(() => {
    if (formik.values.start_date && formik.values.end_date) {
      // Clear previous result before fetching new assessment
      setAssessmentResult(null);
      
      if (props.type === "agingList") {
        assessAgingMutation.mutate({
          assignee: null,
          division: null,
          start_date: formik.values.start_date,
          end_date: formik.values.end_date,
          only_canceled: undefined,
          only_open_with_canceled_appointments: undefined,
          itemized: undefined,
        });
      } else if (props.type === "appointmentList") {
        assessAppointmentMutation.mutate({
          assignee: null,
          division: null,
          start_date: formik.values.start_date,
          end_date: formik.values.end_date,
          only_canceled: undefined,
          only_open_with_canceled_appointments: undefined,
          itemized: undefined,
        });
      } else if (props.type === "requestList") {
        assessRequestMutation.mutate({
          assignee: null,
          division: null,
          start_date: formik.values.start_date,
          end_date: formik.values.end_date,
          only_canceled: formik.values.only_canceled,
          only_open_with_canceled_appointments: formik.values.only_open_with_canceled_appointments,
          itemized: undefined,
        });
      } else if (props.type === "invoiceList") {
        assessInvoiceMutation.mutate({
          assignee: null,
          division: null,
          start_date: formik.values.start_date,
          end_date: formik.values.end_date,
          only_canceled: undefined,
          only_open_with_canceled_appointments: undefined,
          itemized: undefined,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.start_date, formik.values.end_date, formik.values.only_canceled, formik.values.only_open_with_canceled_appointments, props.type]);

  return (
    <div className="w-[500px] flex flex-col">
      <form
        onSubmit={formik.handleSubmit}
        className="flex-1 flex flex-col space-y-4"
      >
        <div className="flex justify-between">
          <Field
            type="date"
            label={tr("Start Date")}
            name="start_date"
            className={"w-[48%]"}
            formik={formik}
          />
          <Field
            type="date"
            label={tr("End Date")}
            name="end_date"
            className={"w-[48%]"}
            formik={formik}
          />
        </div>
        
        {/* Display assessment results for aging, appointmentList, requestList, and invoiceList reports */}
        {(props.type === "agingList" || props.type === "appointmentList" || props.type === "requestList" || props.type === "invoiceList") && (
          <div className="mt-4">
            {(assessAgingMutation.isLoading || assessAppointmentMutation.isLoading || assessRequestMutation.isLoading || assessInvoiceMutation.isLoading) && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-blue-700 text-sm">Assessing report size...</p>
              </div>
            )}
            
            {((props.type === "agingList" && assessAgingMutation.isError) || 
              (props.type === "appointmentList" && assessAppointmentMutation.isError) ||
              (props.type === "requestList" && assessRequestMutation.isError) ||
              (props.type === "invoiceList" && assessInvoiceMutation.isError)) && 
             !(assessAgingMutation.isLoading || assessAppointmentMutation.isLoading || assessRequestMutation.isLoading || assessInvoiceMutation.isLoading) && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                <p className="text-red-700 text-sm font-semibold">Failed to assess report size</p>
                <p className="text-red-600 text-xs mt-1">Check console for details. The API endpoint may not be available yet.</p>
              </div>
            )}
            
            {assessmentResult && !(assessAgingMutation.isLoading || assessAppointmentMutation.isLoading || assessRequestMutation.isLoading || assessInvoiceMutation.isLoading) && (
              <div className={`p-4 rounded-md border ${
                assessmentResult.willTimeout 
                  ? 'bg-red-50 border-red-200' 
                  : assessmentResult.estimatedBatches > 1
                  ? 'bg-yellow-50 border-yellow-200'
                  : 'bg-green-50 border-green-200'
              }`}>
                <div className="space-y-2">
                  <p className={`text-sm font-semibold ${
                    assessmentResult.willTimeout 
                      ? 'text-red-700' 
                      : assessmentResult.estimatedBatches > 1
                      ? 'text-yellow-700'
                      : 'text-green-700'
                  }`}>
                  </p>
                  
                  <div className="text-xs space-y-1 text-gray-700">
                    <p><strong>Total Records:</strong> {assessmentResult.totalRecords.toLocaleString()}</p>
                    <p><strong>Estimated Batches:</strong> {assessmentResult.estimatedBatches}</p>
                    <p><strong>Estimated Time:</strong> {assessmentResult.estimatedTimeMinutes} minute(s)</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        
        { 
          props.type === "requestList" && (
            <>
            <div style={{display: "flex", alignItems: "center"}}>
              <div>
                <Checkbox
                  checked={formik.values.only_canceled}
                  onChange={(e) => {
                    formik.setFieldValue("only_canceled", e.target.checked);
                    if (e.target.checked) {
                      formik.setFieldValue("only_open_with_canceled_appointments", false);
                    }
                  }}
                />
              </div>
              <p>{tr("Retrieve only canceled requests")}</p>
            </div>
            <div style={{display: "flex", alignItems: "center"}}>
              <div>
                <Checkbox
                  checked={formik.values.only_open_with_canceled_appointments}
                  onChange={(e) => {
                    formik.setFieldValue("only_open_with_canceled_appointments", e.target.checked);
                    if (e.target.checked) {
                      formik.setFieldValue("only_canceled", false);
                    }
                  }}
                />
              </div>
              <p>{tr("Retrieve only open requests with canceled appointments")}</p>
            </div>
            </>
          )  
        }
        { 
          props.type === "commissionList" && (
            <div style={{display: "flex", alignItems: "center"}}>
              <div>
                <Checkbox
                  checked={formik.values.itemized}
                  onChange={(e) => formik.setFieldValue("itemized", e.target.checked)}
                />
              </div>
              <p>{tr("Itemize Invoices")}</p>
            </div>
          )  
        }
        { !excludeAssigneeAndDivision.includes(props.type) && (
          <>
            <div>
              <Assignee
                label={tr("Assignees")}
                placeholder={tr("All Assignee")}
                onChange={(e: any) =>
                  formik.setFieldValue(
                    "assignee",
                    e?.map((assignee: any) => assignee.value) || null
                  )
                }
              />
            </div>
            <div>
              <DivisionDropdown
                label={tr("Divisions")}
                placeholder={tr("All Division")}
                isMulti
                onChange={(e: any) =>
                  formik.setFieldValue(
                    "division",
                    e?.map((div: any) => div.id) || null
                  )
                }
              />
            </div>
          </>
        )}
        <div className="mt-auto flex justify-end">
          <Button primary type="submit" isLoading={mutation.isLoading || agingReportMutation.isLoading || commissionReportMutation.isLoading}>
            {tr ("Download Report")}
          </Button>
        </div>
      </form>
    </div>
  );
}
